import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { SharedEligibilityService } from "components/payers/eligibility/shared-eligibility";
import { GeneralPayer } from "components/payers/general-payer/general-payer";
import {
  AlertController,
  Events,
  LoadingController,
  ModalController,
  ModalOptions,
  NavController,
  NavParams,
} from "ionic-angular";
import { Payer } from "models/payers/payer";
import { PayersService } from "providers/payers-service/payers.service";
import { AccessControlService } from "../../../../app/access-control/access-control-service";
import { AuthService } from "../../../../app/auth/auth.service";
import { NavBarServiceProvider } from "../../../../providers/nav-bar-service/nav-bar-service";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { UsersService } from "../../../../providers/users-service/users.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { PAYER_CLASSIFICATION } from "../../../../app/access-control/common-permission-asks";
import { PayerClassification } from "../../../../app/access-control/permissions";

@Component({
  selector: "create-payer",
  templateUrl: "create-payer.html",
})
export class CreatePayerPage implements OnInit {
  @ViewChild(GeneralPayer) generalPayer: GeneralPayer;

  parentForm: FormGroup;
  touched: boolean;
  _saveEnabled: boolean = false;
  _isEditMode: boolean = false;
  currentBenefitType: any;
  currentPayerType: any;
  medicarePayers: any = [];
  changedTabs: any = {};
  showScheduledEligibility: boolean = false;
  showInstantEligibility: boolean = false;

  tabs = [
    {
      id: "generalDetails",
      title: "General",
      selected: true,
      icon: "md-add",
      visible: true,
    },
    {
      id: "billingOptions",
      title: "Billing Options",
      selected: false,
      icon: "md-add",
      visible: true,
    },
    {
      id: "billingCodes",
      title: "Billing Codes",
      selected: false,
      icon: "md-add",
      visible: true,
    },
    {
      id: "rates",
      title: "Rates",
      selected: false,
      icon: "md-add",
      visible: true,
    },
    {
      id: "edi",
      title: "EDI",
      selected: false,
      icon: "md-add",
      visible: true,
    },
    {
      id: "requirements",
      title: "Requirements",
      selected: false,
      icon: "md-add",
      visible: false,
    },
    {
      id: "eligibility",
      title: "Eligibility",
      selected: false,
      icon: "md-add",
      visible: false,
    },
  ];

  payerData: any;
  activeTab = this.tabs[0];
  activeTabId = this.activeTab.id;
  originalPayerType = null;
  payerChangedToMedicare = false;
  originalBenefitType = null;
  benefitTypeChagned = false;

  cardHeader: string = "Payers";

  get saveEnabled() {
    return this._saveEnabled;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get isEditMode() {
    return this._isEditMode;
  }

  get currentPayer() {
    return this.payerData;
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected authSvc: AuthService,
    protected navBarSvc: NavBarServiceProvider,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected modalController: ModalController,
    protected resourceManagementService: ResourceManagementService,
    protected loader: LoadingController,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected fb: FormBuilder,
    protected alertController: AlertController,
    protected events: Events,
    private utilityService: UtilityService,
    private payerService: PayersService,
    public modalCtrl: ModalController,
    private cdr: ChangeDetectorRef,
    private payersService: PayersService,
    private stateData: StateManagementService,
    public sharedEligibility: SharedEligibilityService
  ) {
    this.payerData = navParams.get("payerData");
    if (this.payerData) {
      this._isEditMode = true;
      this.checkPayerEDI();
      this.checkPayerRequirements();
      this.getPayerContractPeriodsForRates();
      this.originalBenefitType = this.payerData.benefitType
        .toUpperCase()
        .split(",");
      this.originalPayerType = this.payerData.payerType.toUpperCase();
    }
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  /**
   * CO-2315 item #6 / F2.S14 — may this user edit payer classification?
   *
   * FALSE UNTIL PROVEN OTHERWISE. payer-functions refuses a classification change with
   * 403 unless the caller holds Company:Payers:Classification:EDIT, so the form must
   * never be more permissive than the API: the controls start read-only and the answer
   * opens them, not the reverse. An ask that is slow, errors, or never returns leaves
   * them read-only, which is the safe direction.
   */
  canEditClassification: boolean = false;

  ngOnInit() {
    this.tabs[0].selected = true;
    this.touched = false;

    // AccessControlService was already injected here and never used (a dead injection
    // predating this story). This is what it was for.
    this.accessControlService.getPermission(PAYER_CLASSIFICATION).subscribe(
      (response) => {
        this.canEditClassification = this.accessControlService.hasAccess(
          response,
          PayerClassification.Edit
        );
      },
      () => {
        // Fail closed. A permission service that cannot answer must not open the controls.
        this.canEditClassification = false;
      }
    );

    this.stateData.showScheduledEligibility.subscribe(
      this.onShowScheduledEligibility.bind(this)
    );

    this.stateData.showInstantEligibilityToggle.subscribe(
      this.onShowInstantEligibility.bind(this)
    );
  }

  ionViewDidLoad() {
    this.stateManagementService.setAssessmentType("Company / Locations");
  }

  cancel() {
    this.presentEditViewPopover();
  }

  protected onShowScheduledEligibility(newValue): void {
    this.showScheduledEligibility = newValue;
    this.updateEligibilityTabVisibility();
  }

  protected onShowInstantEligibility(newValue): void {
    this.showInstantEligibility = newValue;
    this.updateEligibilityTabVisibility();
  }

  private updateEligibilityTabVisibility(): void {
    const showEligibilityTab =
      this.showScheduledEligibility || this.showInstantEligibility;
    const eligibilityTab = this.tabs.find((tab) => tab.id === "eligibility");
    if (eligibilityTab) {
      eligibilityTab.visible = showEligibilityTab;
    }
  }

  presentEditViewPopover() {
    const deleteModalOptions: ModalOptions = {
      enableBackdropDismiss: false,
      cssClass: "delete-facility-confirmation-popup",
    };

    let modalPage = this.modalCtrl.create(
      "CancelFacilityPage",
      {
        title: "Discard Changes",
      },
      deleteModalOptions
    );
    modalPage.onDidDismiss((data) => {
      if (data.cancel) {
        this.close();
      }
    });
    modalPage.present();
  }

  close() {
    this.events.unsubscribe("currentData");
    if (this.navParams.data.parent) {
      this.navParams.data.parent.getPayers();
    }
    this.navCtrl.pop();
  }

  benefitTypeChanged(value) {
    this.currentBenefitType = value;
    this.checkTabVisibility();
    if (this.originalBenefitType && value.length > 0) {
      this.benefitTypeChagned = !this.benefitTypesMatch(
        this.originalBenefitType,
        value
      );
    }
  }

  checkTabVisibility() {
    this.tabs = this.tabs.map((tab) => {
      switch (tab.id) {
        case "billingCodes":
          tab.visible =
            this.currentPayer &&
            this.currentPayer.benefitType.length > 0 &&
            (this.benefitTypeIsHospiceOrRoomAndBoard() ||
              this.currentPayer.benefitType
                .toString()
                .toUpperCase()
                .includes("PALLIATIVE"));
          break;
        case "billingOptions":
          tab.visible =
            this.currentPayer &&
            this.currentPayer.benefitType.length > 0 &&
            this.benefitTypeIsHospiceOrRoomAndBoard();
          break;
        case "rates":
          tab.visible =
            this.currentPayer &&
            this.currentPayer.payerType &&
            this.currentPayer.benefitType;
          break;
        case "requirements":
          tab.visible =
            this.currentPayer &&
            this.currentPayer.benefitType.length > 0 &&
            (this.getPayerType() === "MEDICARE"
              ? this.benefitTypeIncludesHospice()
              : this.benefitTypeIsHospiceOrRoomAndBoard());
          break;
        case "eligibility":
          tab.visible =
            this.showScheduledEligibility || this.showInstantEligibility;
          break;
        default:
          break;
      }
      return tab;
    });
  }

  private benefitTypeIsHospiceOrRoomAndBoard() {
    if (!this.currentPayer?.benefitType) {
      return false;
    }
    let benefitType = this.currentPayer.benefitType.toString().toUpperCase();
    return (
      benefitType.includes("HOSPICE") ||
      benefitType.includes("ROOM AND BOARD") ||
      benefitType.includes("ROOMANDBOARD")
    );
  }

  private benefitTypeIncludesHospice() {
    if (!this.currentPayer?.benefitType) {
      return false;
    }
    return this.currentPayer.benefitType
      .toString()
      .toUpperCase()
      .includes("HOSPICE");
  }

  getPayerType() {
    return this.currentPayer
      ? this.currentPayer.payerType.toString().toUpperCase()
      : null;
  }

  getBenefitType() {
    return this.currentPayer
      ? this.currentPayer.benefitType.toString().toUpperCase()
      : null;
  }

  payerTypeChanged(value) {
    this.currentPayerType = value;
    this.checkTabVisibility();
    if (this.currentPayerType && this.currentPayerType == "MEDICARE") {
      this.removeCirclesFromTabs();
      if (this.originalPayerType && value.length > 0) {
        this.payerChangedToMedicare =
          this.originalPayerType != value.toUpperCase();
      }
    } else {
      this.restoreCirclesFromTabs();
      this.getPayerContractPeriodsForRates();
      this.checkPayerEDI();
    }
  }

  restoreCirclesFromTabs() {
    this.tabs = this.tabs.map((tab) => {
      if (tab.icon == "md-checkmark") return tab;
      tab.icon = "md-add";
      return tab;
    });
  }

  removeCirclesFromTabs() {
    this.tabs = this.tabs.map((tab) => {
      switch (tab.id) {
        case "rates":
          tab.icon = null;
          break;
      }
      return tab;
    });
  }

  save(event: Payer) {
    this.create(event);
  }

  savePayerDetails(payer, spinnerMessage) {
    this.utilityService.increaseSpinnerCount(spinnerMessage);
    this.payerService
      .savePayerDetails(payer)
      .pipe()
      .take(1)
      .subscribe((data) => {
        this.onSaveSuccess(data);
      }, this.onSaveError);
  }

  private onSaveSuccess = (data: any) => {
    setTimeout(() => {
      this.utilityService.decreaseSpinnerCount();
      this.saveEnabled = false;
      this.payerData = data;
      this.originalPayerType = data.payerType.toUpperCase();
      this.originalBenefitType = data.benefitType.toUpperCase().split(",");
      if (this.showScheduledEligibility) {
        data.eligibilityUpdated = true;
        this.events.publish("currentData", data);
      }
      if (this.validateCheckMark(data)) this.tabs[0].icon = "md-checkmark";
      else this.tabs[0].icon = "md-add";
      this.changedTabs["generalDetails"] = false;
      this.touched = false;
      this.cdr.detectChanges();
      this.onSaveSuccessAlert();
      this.checkTabVisibility();
    }, 2000);
  };

  validateCheckMark(data) {
    if (
      data.payerName == null ||
      data.payerName.length == 0 ||
      data.payerType == null ||
      data.payerType.length == 0 ||
      data.benefitType == null ||
      data.benefitType.length == 0
    )
      return false;
    let planAddress = data.plans.find(
      ({ planName }) => planName === "PlanAddress"
    );
    if (planAddress == undefined) return false;
    return !(
      planAddress.city == null ||
      planAddress.city.length == 0 ||
      planAddress.state == null ||
      planAddress.state.length == 0 ||
      planAddress.zipCode == null ||
      planAddress.zipCode.length == 0 ||
      planAddress.zipCodeExtension == null ||
      planAddress.zipCodeExtension.length == 0 ||
      planAddress.phoneNumber == null ||
      planAddress.phoneNumber.length == 0
    );
  }

  validateExistingMedicarePayers(payer) {
    if (payer.payerId) {
      this.medicarePayers = this.medicarePayers.filter(
        (hit) => hit.payerId != payer.payerId
      );
    }
    if (
      !payer.payerId ||
      (payer.payerId &&
        (this.payerChangedToMedicare || this.benefitTypeChagned))
    ) {
      if (
        payer.payerType.toUpperCase() == "MEDICARE" &&
        this.medicarePayers &&
        this.medicarePayers.length > 0
      ) {
        let benefitTypes = payer.benefitType.split(",");
        return (
          this.medicarePayers.filter((medPayer) => {
            return medPayer.benefitType
              .split(",")
              .some((bt) => benefitTypes.includes(bt));
          }).length == 0
        );
      }
    }

    return true;
  }

  private onSaveSuccessAlert = () => {
    this.showAlert({
      title: "Success",
      message: "Payer Saved",
    });
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      this.showAlert({
        title: "Error",
        message: this.extractSaveErrorMessage(error),
      });
    }
  };

  // CO-3592: payer-functions error responses (e.g. the CO-3392 duplicate
  // state-FFS rejection) carry the message as a RAW TEXT body —
  // services.HandleError -> util.NewResponse writes err.Error() with no
  // JSON envelope — so HttpClient's JSON parse fails and `error.error` is
  // the plain string. Reading `error.error.message` was undefined and the
  // dialogue rendered empty. Mirrors the NOVA-1571 extraction in
  // doc-management-data-service, plus the raw-string case this API needs.
  private extractSaveErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;
    if (typeof body === "string" && body.trim().length > 0) {
      try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed.message === "string" && parsed.message) {
          return parsed.message;
        }
      } catch {
        // Not JSON — the body itself is the server's message.
      }
      return body;
    }
    if (body && typeof body.message === "string" && body.message) {
      return body.message;
    }
    return error.message || "The payer could not be saved. Please try again.";
  }

  setTouched(touched: boolean, tabId) {
    this.changedTabs[tabId] = touched;
    for (let id of Object.keys(this.changedTabs)) {
      if (this.changedTabs[id]) {
        let index = this.tabs.indexOf(this.tabs.find((t) => t.id === tabId));
        if (index >= 0) {
          this.tabs[index].icon = "md-radio-button-off";
          return;
        }
      }
    }
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  private create(obj) {
    this.saveEnabled = false;
    let message = "Creating Payer ...";
    if (obj.payerId && this.isEditMode) {
      this.showSavePayerModal(obj);
    } else {
      this.savePayerDetails(obj, message);
    }
  }

  showSavePayerModal(payer) {
    let modalPage = this.modalCtrl.create(
      "SaveFacilityPage",
      {
        title: "Payer Information Change",
        commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
        popupMessageKey: "savePayerWarningMessage",
        section: "payer",
      },
      {
        enableBackdropDismiss: false,
        cssClass: "save-payer-confirmation-popup",
      }
    );
    modalPage.onDidDismiss((data) => {
      if (data.save) {
        this.savePayerDetails(payer, "Updating Payer ...");
      }
    });
    modalPage.present();
  }

  selectTab(selectedTab) {
    // update tabs
    this.tabs = this.tabs.map((tab) => {
      tab.selected = tab.id === selectedTab.id;
      return tab;
    });

    // update activeTab key for sections displayed
    this.activeTab = selectedTab;
    this.activeTabId = selectedTab.id;
  }

  getPayerContractPeriodsForRates() {
    if (this.currentPayer && this.currentPayer.payerId) {
      this.payersService
        .getPayerRatesPeriods(this.currentPayer.payerId, "HOSPICE", "VISITS")
        .pipe()
        .take(1)
        .subscribe((response) => {
          if (response !== null) {
            this.checkPayerRatesPeriodsResponse(response);
          }
        });
      this.payersService
        .getPayerRatesPeriods(this.currentPayer.payerId, "HOSPICE", "LOC")
        .pipe()
        .take(1)
        .subscribe((response) => {
          if (response !== null) {
            this.checkPayerRatesPeriodsResponse(response);
          }
        });
      this.payersService
        .getPayerRatesPeriods(this.currentPayer.payerId, "PALLIATIVE", null)
        .pipe()
        .take(1)
        .subscribe((response) => {
          if (response !== null) {
            this.checkPayerRatesPeriodsResponse(response);
          }
        });
    }
  }

  checkPayerRatesPeriodsResponse(response) {
    let payerPeriods;
    const responseArray = Array.isArray(response) ? response : [response];
    payerPeriods = [...responseArray];
    if (payerPeriods.length > 0) {
      this.setCheckmarkForRatesTab();
    }
  }

  setCheckmarkForRatesTab() {
    this.tabs = this.tabs.map((tab) => {
      if (tab.id == "rates") {
        tab.icon = "md-checkmark";
        this.changedTabs[tab.id] = false;
      }
      return tab;
    });
  }

  setCheckmarkForTab(tabId) {
    this.tabs = this.tabs.map((tab) => {
      if (tab.id == tabId) {
        tab.icon = "md-checkmark";
      }
      return tab;
    });
  }

  checkPayerEDI() {
    if (
      this.payerData &&
      this.payerData.claimRules &&
      Array.isArray(this.payerData.claimRules.edi)
    ) {
      this.setCheckmarkForEDITab();
    }
  }

  setCheckmarkForEDITab() {
    this.tabs = this.tabs.map((tab) => {
      if (tab.id == "edi") {
        tab.icon = "md-checkmark";
      }
      return tab;
    });
  }

  setCheckmarkForEligibilityTab() {
    this.tabs = this.tabs.map((tab) => {
      if (tab.id == "eligibility") {
        tab.icon = "md-checkmark";
      }
      return tab;
    });
  }

  checkPayerRequirements() {
    if (
      this.currentPayer &&
      this.currentPayer.complianceRules &&
      this.currentPayer.complianceRules.updated
    ) {
      this.setAddForRequirementsTab();
    }
  }

  setAddForRequirementsTab() {
    this.tabs = this.tabs.map((tab) => {
      if (tab.id == "requirements") {
        tab.icon = "md-add";
      }
      return tab;
    });
  }

  createSearchRequestBody() {
    let details: any = {};
    details.dataType = "PayerConfig";
    details.mustFields = [
      {
        name: "dataType",
        value: "PayerConfig",
      },
      {
        name: "payerType",
        value: "MEDICARE",
      },
    ];
    details.SearchString = null;
    details.dateRange = null;
    details.OptionalFields = [];
    details.sortBy = {
      name: "payerName",
      value: "asc",
    };
    details.From = 0;
    details.Size = 10;
    return details;
  }

  async getMedicarePayers(): Promise<void> {
    return new Promise((resolve, reject) => {
      let requestBody = this.createSearchRequestBody();
      this.payersService.searchPayersFromOS(requestBody).subscribe(
        (response) => {
          if (
            response !== null &&
            response["hits"] !== null &&
            response["hits"].length > 0
          ) {
            this.medicarePayers = response["hits"];
            resolve();
          } else {
            this.medicarePayers = [];
            resolve();
          }
        },
        (err) => {
          this.medicarePayers = [];
          reject(err);
        }
      );
    });
  }

  benefitTypesMatch(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) return false;

    const set1 = new Set(arr1);
    const set2 = new Set(arr2);

    return Array.from(set1).every((item) => set2.has(item));
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/payers-view/create-payer/create-payer.ts